/**
 * Servicio de cache de transacciones en localStorage
 */

import { Transaction, TransactionCache } from '@/types/transaction.types';
import { SYNC_CONFIG } from '@/config/transaction-networks';

const CACHE_KEY = 'transaction_cache';

export class TransactionCacheService {
  /**
   * Obtiene todas las transacciones del cache para una wallet y red
   */
  static getTransactions(walletAddress: string, network: string): Transaction[] {
    try {
      const cache = this.getCache();
      return cache[walletAddress]?.[network]?.transactions || [];
    } catch (error) {
      console.error('[TransactionCache] Error getting transactions:', error);
      return [];
    }
  }

  /**
   * Guarda transacciones en el cache
   */
  static saveTransactions(
    walletAddress: string, 
    network: string, 
    transactions: Transaction[],
    lastBlock?: number
  ): void {
    try {
      const cache = this.getCache();
      
      if (!cache[walletAddress]) {
        cache[walletAddress] = {};
      }
      
      if (!cache[walletAddress][network]) {
        cache[walletAddress][network] = {
          transactions: [],
          lastUpdate: 0,
          lastBlock: 0,
        };
      }

      // Mergear transacciones existentes con nuevas
      const existingTxs = cache[walletAddress][network].transactions;
      const mergedTxs = this.mergeTransactions(existingTxs, transactions);

      cache[walletAddress][network] = {
        transactions: mergedTxs,
        lastUpdate: Date.now(),
        lastBlock: lastBlock || this.getHighestBlockNumber(mergedTxs),
      };

      this.saveCache(cache);
      console.log(`[TransactionCache] Saved ${transactions.length} transactions for ${walletAddress}/${network}`);
    } catch (error) {
      console.error('[TransactionCache] Error saving transactions:', error);
    }
  }

  /**
   * Agrega una transacción individual (para transacciones enviadas por el usuario)
   */
  static addTransaction(walletAddress: string, network: string, transaction: Transaction): void {
    try {
      const existingTxs = this.getTransactions(walletAddress, network);
      
      // Verificar si la transacción ya existe
      const exists = existingTxs.some(tx => tx.hash === transaction.hash);
      if (exists) {
        console.log(`[TransactionCache] Transaction ${transaction.hash} already exists`);
        return;
      }

      const updatedTxs = [transaction, ...existingTxs];
      this.saveTransactions(walletAddress, network, updatedTxs);
    } catch (error) {
      console.error('[TransactionCache] Error adding transaction:', error);
    }
  }

  /**
   * Actualiza el estado de una transacción
   */
  static updateTransactionStatus(
    walletAddress: string, 
    network: string, 
    txHash: string, 
    status: 'pending' | 'confirmed' | 'failed',
    blockNumber?: number
  ): void {
    try {
      const transactions = this.getTransactions(walletAddress, network);
      const txIndex = transactions.findIndex(tx => tx.hash === txHash);
      
      if (txIndex === -1) {
        console.warn(`[TransactionCache] Transaction ${txHash} not found for status update`);
        return;
      }

      transactions[txIndex].status = status;
      if (blockNumber) {
        transactions[txIndex].blockNumber = blockNumber;
      }

      this.saveTransactions(walletAddress, network, transactions);
      console.log(`[TransactionCache] Updated transaction ${txHash} status to ${status}`);
    } catch (error) {
      console.error('[TransactionCache] Error updating transaction status:', error);
    }
  }

  /**
   * Obtiene transacciones pendientes
   */
  static getPendingTransactions(walletAddress: string, network: string): Transaction[] {
    const transactions = this.getTransactions(walletAddress, network);
    return transactions.filter(tx => tx.status === 'pending');
  }

  /**
   * Verifica si el cache está desactualizado
   */
  static isCacheStale(walletAddress: string, network: string): boolean {
    try {
      const cache = this.getCache();
      const networkCache = cache[walletAddress]?.[network];
      
      if (!networkCache) return true;
      
      const timeSinceUpdate = Date.now() - networkCache.lastUpdate;
      return timeSinceUpdate > SYNC_CONFIG.CACHE_TTL;
    } catch (error) {
      console.error('[TransactionCache] Error checking cache staleness:', error);
      return true;
    }
  }

  /**
   * Obtiene el último bloque sincronizado
   */
  static getLastSyncedBlock(walletAddress: string, network: string): number {
    try {
      const cache = this.getCache();
      return cache[walletAddress]?.[network]?.lastBlock || 0;
    } catch (error) {
      console.error('[TransactionCache] Error getting last synced block:', error);
      return 0;
    }
  }

  /**
   * Limpia el cache para una wallet específica
   */
  static clearWalletCache(walletAddress: string): void {
    try {
      const cache = this.getCache();
      delete cache[walletAddress];
      this.saveCache(cache);
      console.log(`[TransactionCache] Cleared cache for wallet ${walletAddress}`);
    } catch (error) {
      console.error('[TransactionCache] Error clearing wallet cache:', error);
    }
  }

  /**
   * Limpia todo el cache
   */
  static clearAllCache(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
      console.log('[TransactionCache] Cleared all transaction cache');
    } catch (error) {
      console.error('[TransactionCache] Error clearing all cache:', error);
    }
  }

  /**
   * Obtiene estadísticas del cache
   */
  static getCacheStats(): { wallets: number; totalTransactions: number; networks: string[] } {
    try {
      const cache = this.getCache();
      const wallets = Object.keys(cache).length;
      let totalTransactions = 0;
      const networksSet = new Set<string>();

      Object.values(cache).forEach(walletCache => {
        Object.entries(walletCache).forEach(([network, networkCache]) => {
          totalTransactions += networkCache.transactions.length;
          networksSet.add(network);
        });
      });

      return {
        wallets,
        totalTransactions,
        networks: Array.from(networksSet),
      };
    } catch (error) {
      console.error('[TransactionCache] Error getting cache stats:', error);
      return { wallets: 0, totalTransactions: 0, networks: [] };
    }
  }

  // Métodos privados

  private static getCache(): TransactionCache {
    try {
      const cacheData = localStorage.getItem(CACHE_KEY);
      if (!cacheData) return {};
      
      const cache = JSON.parse(cacheData) as TransactionCache;
      
      // Auto-corrección de timestamps (migración de segundos a milisegundos)
      let modified = false;
      Object.values(cache).forEach(walletCache => {
        Object.values(walletCache).forEach(networkCache => {
          networkCache.transactions.forEach(tx => {
            // Si el timestamp es menor a 10^12, probablemente está en segundos
            if (tx.timestamp < 10000000000) {
              tx.timestamp = tx.timestamp * 1000;
              modified = true;
            }
          });
        });
      });
      
      if (modified) {
        this.saveCache(cache);
      }
      
      return cache;
    } catch (error) {
      console.error('[TransactionCache] Error parsing cache data:', error);
      return {};
    }
  }

  private static saveCache(cache: TransactionCache): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.error('[TransactionCache] Error saving cache:', error);
    }
  }

  private static mergeTransactions(existing: Transaction[], newTxs: Transaction[]): Transaction[] {
    const txMap = new Map<string, Transaction>();

    // Agregar transacciones existentes
    existing.forEach(tx => txMap.set(tx.hash, tx));

    // Agregar/actualizar con nuevas transacciones
    newTxs.forEach(tx => {
      const existingTx = txMap.get(tx.hash);
      if (existingTx) {
        // Siempre merge con los datos nuevos tomando prioridad.
        // No descartar si ambos son confirmed — el valor puede haber sido corregido.
        // Preservar campos del existente que no vengan en el nuevo (ej. gasUsed).
        txMap.set(tx.hash, { ...existingTx, ...tx });
      } else {
        txMap.set(tx.hash, tx);
      }
    });

    // Convertir a array y ordenar por timestamp descendente
    return Array.from(txMap.values()).sort((a, b) => b.timestamp - a.timestamp);
  }

  private static getHighestBlockNumber(transactions: Transaction[]): number {
    return transactions.reduce((max, tx) => Math.max(max, tx.blockNumber), 0);
  }
}